import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";

export const RoyalListForm = ({ source = "homepage", light = false }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await api.post("/royal-list", {
        first_name: firstName,
        email,
        source,
        website,
      });
      setStatus(data.status === "already" ? "already" : "done");
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err));
    }
  };

  if (status === "done" || status === "already") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        data-testid="royal-list-confirmation"
        className={`border p-10 sm:p-14 text-center ${light ? "border-wine/30 bg-white/50" : "border-gold/30 bg-gallery/60"}`}
      >
        <p className={`font-mono text-[11px] tracking-[0.35em] uppercase mb-4 ${light ? "text-wine" : "text-gold"}`}>
          {status === "already" ? "You're already royalty" : "Confirmed"}
        </p>
        <p className={`font-serif text-3xl sm:text-4xl tracking-tight ${light ? "text-obsidian" : "text-cream"}`}>
          WELCOME TO THE ROYAL LIST.
        </p>
        <p className={`mt-4 text-sm ${light ? "text-obsidian/60" : "text-sand"}`}>
          {status === "already"
            ? "This email is already on the list — you're all set."
            : "The next Vault drop will find you first. Straight from the Queen."}
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="royal-list-form" className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="text"
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First Name"
          aria-label="First Name"
          data-testid="first-name-input"
          className={`h-14 px-5 text-sm bg-transparent border outline-none transition-colors duration-300 ${
            light
              ? "border-obsidian/25 text-obsidian placeholder:text-obsidian/40 focus:border-wine"
              : "border-gold/25 text-cream placeholder:text-sand/50 focus:border-gold"
          }`}
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email Address"
          aria-label="Email Address"
          data-testid="email-capture-input"
          className={`h-14 px-5 text-sm bg-transparent border outline-none transition-colors duration-300 ${
            light
              ? "border-obsidian/25 text-obsidian placeholder:text-obsidian/40 focus:border-wine"
              : "border-gold/25 text-cream placeholder:text-sand/50 focus:border-gold"
          }`}
        />
      </div>
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        name="website"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="email-capture-submit-btn"
        className={`mt-4 w-full h-14 font-mono text-[11px] tracking-[0.3em] uppercase transition-colors duration-300 ${
          light
            ? "bg-wine text-cream hover:bg-wineaccent"
            : "bg-gold text-obsidian hover:bg-goldhover"
        } disabled:opacity-60`}
      >
        {status === "loading" ? "Joining..." : "JOIN THE ROYAL LIST"}
      </button>
      {status === "error" && (
        <p data-testid="royal-list-error" className="mt-3 text-sm text-wineaccent">{message}</p>
      )}
      <p className={`mt-4 text-[11px] leading-relaxed ${light ? "text-obsidian/50" : "text-sand/60"}`}>
        By joining the Royal List, you agree to receive occasional emails from Queen Denae about Royal Vault drops, new music, videos, events, and exclusive content. You can unsubscribe at any time. See our{" "}
        <Link to="/privacy" className={`underline ${light ? "text-wine" : "text-gold/80"}`}>Privacy Policy</Link>.
      </p>
    </form>
  );
};
